import { getAnalyticsEvents } from './analyticsService';
import { getAllSubscriptions } from './subscriptionService';
import { getCollection } from './firestoreService';
import { differenceInDays } from 'date-fns';

export interface UserSegment {
  segmentName: string;
  count: number;
  percentage: number;
  retentionRate: number;
  revenue: number;
  avgSessionsPerWeek: number;
}

export interface SegmentationAnalytics {
  segments: UserSegment[];
  segmentComparison: {
    retention: Map<string, number>;
    revenue: Map<string, number>;
    featureUsage: Map<string, Map<string, number>>;
  };
}

export const getSegmentationAnalytics = async (
  days: number = 30
): Promise<SegmentationAnalytics> => {
  try {
    const rawUsers = await getCollection('users', undefined, undefined, undefined, 10000);
    const users = rawUsers.filter((u: any) => !u.deletedAt && !u.isDeleted);
    const events = await getAnalyticsEvents(days);
    const subscriptions = await getAllSubscriptions();

    // Calculate sessions per user
    const userSessions = new Map<string, number>();
    events.forEach((event) => {
      if (event.eventName === 'session_start' || event.eventName === 'app_launch') {
        userSessions.set(event.userId, (userSessions.get(event.userId) || 0) + 1);
      }
    });

    // Segment users
    const segments = new Map<string, Set<string>>();
    const now = new Date();

    users.forEach((user: any) => {
      const userId = user.id;
      const signupDate = user.createdAt?.toDate ? user.createdAt.toDate() : new Date(user.createdAt);
      const daysSinceSignup = differenceInDays(now, signupDate);
      const sessionsCount = userSessions.get(userId) || 0;
      const sessionsPerWeek = daysSinceSignup > 0 ? (sessionsCount / daysSinceSignup) * 7 : 0;
      const isSubscribed = subscriptions.some((s: any) => s.userId === userId && s.status === 'active');
      const lastSeen = user.lastSeen?.toDate ? user.lastSeen.toDate() : null;
      const daysSinceLastSeen = lastSeen ? differenceInDays(now, lastSeen) : daysSinceSignup;

      // Power users: >10 sessions/week
      if (sessionsPerWeek > 10) {
        if (!segments.has('Power Users')) segments.set('Power Users', new Set());
        segments.get('Power Users')!.add(userId);
      }
      // Casual users: 1-3 sessions/week
      else if (sessionsPerWeek >= 1 && sessionsPerWeek <= 3) {
        if (!segments.has('Casual Users')) segments.set('Casual Users', new Set());
        segments.get('Casual Users')!.add(userId);
      }
      // Dormant users: no activity 30+ days
      else if (daysSinceLastSeen >= 30) {
        if (!segments.has('Dormant Users')) segments.set('Dormant Users', new Set());
        segments.get('Dormant Users')!.add(userId);
      }
      // New users: <7 days
      else if (daysSinceSignup < 7) {
        if (!segments.has('New Users')) segments.set('New Users', new Set());
        segments.get('New Users')!.add(userId);
      }

      // Subscribed vs Free
      if (isSubscribed) {
        if (!segments.has('Subscribed')) segments.set('Subscribed', new Set());
        segments.get('Subscribed')!.add(userId);
      } else {
        if (!segments.has('Free')) segments.set('Free', new Set());
        segments.get('Free')!.add(userId);
      }
    });

    // Calculate segment metrics
    const userSegments: UserSegment[] = [];

    segments.forEach((userIds, segmentName) => {
      const count = userIds.size;
      const percentage = users.length > 0 ? (count / users.length) * 100 : 0;

      // Calculate retention (users who returned in last 7 days)
      const recentActiveUsers = new Set(
        events
          .filter((e) => {
            const eventDate = e.timestamp;
            const daysSinceEvent = differenceInDays(now, eventDate);
            return daysSinceEvent <= 7;
          })
          .map((e) => e.userId)
      );

      const retainedUsers = Array.from(userIds).filter((uid) => recentActiveUsers.has(uid)).length;
      const retentionRate = count > 0 ? (retainedUsers / count) * 100 : 0;

      // Calculate revenue
      const segmentRevenue = subscriptions
        .filter((s: any) => userIds.has(s.userId) && s.status === 'active')
        .reduce((sum, s: any) => sum + (s.amount || 0), 0);

      // Calculate avg sessions per week
      let totalSessions = 0;
      userIds.forEach((userId) => {
        totalSessions += userSessions.get(userId) || 0;
      });
      const avgSessionsPerWeek = count > 0 ? (totalSessions / count) / (days / 7) : 0;

      userSegments.push({
        segmentName,
        count,
        percentage,
        retentionRate,
        revenue: segmentRevenue,
        avgSessionsPerWeek,
      });
    });

    // Segment comparison
    const retentionMap = new Map<string, number>();
    const revenueMap = new Map<string, number>();
    const featureUsageMap = new Map<string, Map<string, number>>();

    userSegments.forEach((segment) => {
      retentionMap.set(segment.segmentName, segment.retentionRate);
      revenueMap.set(segment.segmentName, segment.revenue);
    });

    return {
      segments: userSegments.sort((a, b) => b.count - a.count),
      segmentComparison: {
        retention: retentionMap,
        revenue: revenueMap,
        featureUsage: featureUsageMap,
      },
    };
  } catch (error) {
    console.error('Error calculating segmentation analytics:', error);
    throw error;
  }
};

